import { useCallback, useEffect, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./ReinstatePaywall.module.scss"

/**
 * ReinstatePaywall (`/reinstate`) — an original, self-contained EXPIRATION
 * paywall for Private Photo Vault Premium. Paywall type rolled at random this
 * run: EXPIRATION (a lapsed-membership win-back screen).
 *
 * Divergence brief + all axes: claude-paywalls/_ledger.md. Concept doc:
 * claude-paywalls/reinstate.md.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * Newest @Paywalls-Bot mention this run: `focus on security and privacy` (NOT
 * the reset keyword `default`) → an ACTIVE STANDING DIRECTIVE at super-high
 * priority. The whole concept is built to it — reframed away from the saturated
 * "who's attacking / where it leaks / fear-of-loss" expiration angles into calm,
 * dignified RESTORATION: your protection didn't vanish, it stood down. Bring it
 * back on line.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * Your vault has two layers. The base layer — your PIN — never dropped; your
 * photos stayed locked exactly where you left them. The Premium layer — the
 * active safeguards working *around* them (encrypted Cloud Vault, Break-In
 * Report, Decoy Passcode, out-of-Camera-Roll import) — went to STANDBY when
 * Premium lapsed. Each safeguard is shown stood-down and quiet. You throw a
 * physical DEADBOLT (press-and-hold): it glides home into its strike plate, a
 * coverage rail lights down the stack, and every safeguard RE-ARMS in a calm
 * top-to-bottom cascade — icon lights, status flips from STANDBY to ARMED. The
 * value is CONTINUITY of protection: nothing was lost; you simply switch your
 * safeguards back on.
 *
 * ── Divergence (vs. the ~110 prior concepts, incl. the 5 expiration screens) ─
 * Layout      : a vertical SECURITY-STACK PANEL (protected memories at the head,
 *               a coverage rail + labelled safeguard rows down the body) — not
 *               the centred single-column pricing scroll every prior expiration
 *               paywall uses, and not Cradle's radial cluster.
 * Interaction : PRESS-AND-HOLD A DEADBOLT that seats home and drives a cascade
 *               re-arm — no prior concept throws a deadbolt (doors have cranks/
 *               dials; this is a linear bolt into a strike plate).
 * Metaphor    : DEADBOLT + a re-arming safeguard stack (systems back on line) —
 *               not a door, dial, mirror, net, seal, key-card, guard-character,
 *               clock, calendar or console.
 * Register    : COMPOSED, DIGNIFIED RESTORATION — quiet competence, no fear
 *               (Deep-Freeze/ThreeStep), no cosiness (Cradle/Hearth), no
 *               credential-desk (Reissue). "Off standby → back on watch."
 * Value frame : CONTINUITY + risk-reversal — "nothing was lost; switch it back
 *               on", 7-day free trial.
 * Motion      : instrument-precise — a bolt glides, a rail energises, rows arm
 *               in sequence; steel/blue turning to a secure green ARMED state.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * No data-loss-on-expiration claim — the copy states plainly that photos stayed
 * locked behind the PIN the whole time; only the *active* Premium safeguards
 * paused. Safeguards are REAL Premium features (encrypted Cloud Vault, Break-In
 * Report, Decoy Passcode, imports leaving the Camera Roll). No countdown, no fake
 * urgency or scarcity, honest "Save %" from live product data. Yearly is the
 * featured, default, most prominent price; monthly is a small secondary line.
 * Bundled demo memories only — never the user's own library.
 */

type PlanKey = "yearly" | "monthly"

// Real personal-memory photos (bundled) protected at the head of the stack.
// Never the user's own library — a demo set, shown in full colour.
const MEMORIES = ["dog-1", "sunset-1", "food-2", "flower-1"]

interface Safeguard {
  key: string
  name: string
  note: string
  icon: JSX.Element
}

const SAFEGUARDS: Safeguard[] = [
  {
    key: "cloud",
    name: "Encrypted Cloud Vault",
    note: "Off-device backup, encrypted end to end — syncing again",
    icon: (
      <>
        <path
          d="M7 17.5h9.5a3.5 3.5 0 0 0 .4-6.98 5 5 0 0 0-9.62-1.2A3.9 3.9 0 0 0 7 17.5z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M12 11.4v3.2M10.4 12.8h3.2"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </>
    ),
  },
  {
    key: "breakin",
    name: "Break-In Report",
    note: "A photo + location captured on a failed unlock attempt",
    icon: (
      <>
        <path
          d="M2.5 12S6 6 12 6s9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="12"
          r="2.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
      </>
    ),
  },
  {
    key: "decoy",
    name: "Decoy Passcode",
    note: "A second passcode opens a harmless stand-in vault",
    icon: (
      <>
        <rect
          x="4.5"
          y="4.5"
          width="10"
          height="13"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <rect
          x="9.5"
          y="6.5"
          width="10"
          height="13"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
      </>
    ),
  },
  {
    key: "import",
    name: "Out of the Camera Roll",
    note: "New imports leave your Camera Roll & shared albums",
    icon: (
      <>
        <path
          d="M12 3.5v9m0-9L8.8 6.7M12 3.5l3.2 3.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5 13.5v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </>
    ),
  },
]

const HOLD_MS = 900

const ReinstatePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = (() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 79
    return Math.round((1 - y / (m * 12)) * 100)
  })()

  const [armed, setArmed] = useState<boolean>(!!reduceMotion)
  const [holding, setHolding] = useState(false)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)

  const touched = useRef(false)
  const holdTimer = useRef<number | null>(null)
  const timers = useRef<number[]>([])
  const pushTimer = (id: number) => timers.current.push(id)

  const reinstate = useCallback(() => {
    setArmed(true)
    setHolding(false)
    native.logMixpanelEvent("html_reinstate_armed")
  }, [])

  const clearHold = () => {
    if (holdTimer.current != null) {
      window.clearTimeout(holdTimer.current)
      holdTimer.current = null
    }
  }

  // Auto-demo for passive viewers: let the stack sit on standby for a beat, then
  // throw the bolt so the payoff is always seen. Reduced motion starts armed.
  useEffect(() => {
    if (reduceMotion) {
      setShowClose(true)
      return
    }
    pushTimer(window.setTimeout(() => setShowClose(true), 1600))
    pushTimer(
      window.setTimeout(() => {
        if (touched.current) return
        setHolding(true)
        holdTimer.current = window.setTimeout(reinstate, HOLD_MS)
      }, 2200),
    )
    return () => {
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
      clearHold()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleHoldStart = () => {
    if (armed) return
    touched.current = true
    setHolding(true)
    clearHold()
    holdTimer.current = window.setTimeout(reinstate, HOLD_MS)
  }
  const handleHoldEnd = () => {
    if (armed) return
    setHolding(false)
    clearHold()
  }

  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct
  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    native.logMixpanelEvent("html_reinstate_subscribe", { plan })
    if (selectedProduct) native.subscribe(selectedProduct)
  }
  const openTerms = () => native.postNativeEvent("terms")
  const openPrivacy = () => native.postNativeEvent("privacy")

  const rootClass = [
    styles.root,
    armed ? styles.rootArmed : "",
    holding ? styles.rootHolding : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <div className={rootClass}>
      <div className={styles.grid} aria-hidden="true" />
      <div className={styles.aura} aria-hidden="true" />

      {/* ---------- header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 11.6l2 2 4-4.2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- title ---------- */}
      <div className={styles.intro}>
        <span className={styles.eyebrow}>
          <span className={styles.eyebrowDot} aria-hidden="true" />
          {armed
            ? "Premium protection · active"
            : "Premium protection · on standby"}
        </span>
        <h1 className={styles.title}>
          Your safeguards are
          <br />
          <span className={styles.titleAccent}>standing by.</span>
        </h1>
        <p className={styles.sub}>
          Your photos never left the vault — they’re locked behind your PIN,
          exactly where you left them. Only the active protection around them
          paused. Reinstate to switch every safeguard back on line.
        </p>
      </div>

      {/* ---------- the security stack ---------- */}
      <div className={styles.stack}>
        {/* protected memories at the head — base PIN never dropped */}
        <div className={styles.head}>
          <div className={styles.memories}>
            {MEMORIES.map((m, i) => (
              <span
                key={m}
                className={styles.memory}
                data-i={i}
                style={{ backgroundImage: `url(/recall/${m}.jpg)` }}
              />
            ))}
            <span className={styles.headSeal} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M7 10V8a5 5 0 0 1 10 0v2M6 10h12v9H6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
          <div className={styles.headMeta}>
            <span className={styles.headTitle}>Your vault</span>
            <span className={styles.baseRow}>
              <span className={styles.baseDot} aria-hidden="true" />
              PIN lock — always on
            </span>
          </div>
        </div>

        {/* coverage rail + safeguard rows */}
        <ul className={styles.rows}>
          <span className={styles.rail} aria-hidden="true">
            <span className={styles.railFill} />
          </span>
          {SAFEGUARDS.map((s, i) => (
            <li
              key={s.key}
              className={styles.row}
              style={{ transitionDelay: `${180 + i * 130}ms` }}
            >
              <span className={styles.node} aria-hidden="true" />
              <span className={styles.rowIcon} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  {s.icon}
                </svg>
              </span>
              <span className={styles.rowText}>
                <span className={styles.rowName}>{s.name}</span>
                <span className={styles.rowNote}>{s.note}</span>
              </span>
              <span className={styles.status}>
                <span className={styles.statusStandby}>Standby</span>
                <span className={styles.statusArmed}>
                  <svg
                    viewBox="0 0 24 24"
                    width="11"
                    height="11"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12.5l4 4 10-10.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Armed
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* readout */}
      <div className={styles.readout} aria-live="polite">
        <span className={styles.readoutDot} aria-hidden="true" />
        <span className={styles.readoutText}>
          {armed
            ? "Reinstated — every safeguard back on watch"
            : "On standby — Premium safeguards paused, photos still locked"}
        </span>
      </div>

      {/* ---------- deadbolt control ---------- */}
      <div className={styles.control}>
        {armed ? (
          <span className={styles.settled}>
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Protection reinstated — you’re fully covered again
          </span>
        ) : (
          <button
            className={styles.bolt}
            onPointerDown={handleHoldStart}
            onPointerUp={handleHoldEnd}
            onPointerLeave={handleHoldEnd}
            onPointerCancel={handleHoldEnd}
            aria-label="Press and hold to reinstate protection"
          >
            <span className={styles.boltFill} aria-hidden="true">
              <span className={styles.boltSlug}>
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    d="M7 10V8a5 5 0 0 1 10 0v2M6 10h12v9H6z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.boltLabel}>
              Hold to reinstate protection
            </span>
            <span className={styles.strike} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* reassurance */}
      <span className={styles.reassure}>
        Nothing was lost — reinstating simply switches your safeguards back on.
      </span>

      {/* ---------- pricing (yearly featured) ---------- */}
      <div className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbon}>SAVE {savingsPct}%</span>
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planMeta}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                7 days free · then {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planRight}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planName}>Monthly</span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <motion.button
        className={styles.cta}
        onClick={handleSubscribe}
        whileTap={{ scale: 0.985 }}
      >
        <span className={styles.ctaMain}>
          {plan === "yearly" ? "Reinstate — 7 days free" : "Reinstate Premium"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `Then ${yearlyPrice}/year · cancel anytime`
            : `${monthlyPrice}/month · cancel anytime`}
        </span>
      </motion.button>

      <div className={styles.legal}>
        <button onClick={openTerms}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={openPrivacy}>Privacy</button>
        <span aria-hidden="true">·</span>
        <span>Auto-renews until cancelled</span>
      </div>
    </div>
  )
}

export default ReinstatePaywall
